/**
 * Dark Mode Context
 * =================
 * Manages dark/light theme toggle with localStorage persistence
 * and system preference detection. Apple-style smooth transitions.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const DarkModeContext = createContext({
  isDark: false,
  toggle: () => {},
  setMode: () => {},
});

export function useDarkMode() {
  return useContext(DarkModeContext);
}

export function DarkModeProvider({ children }) {
  const [isDark, setIsDark] = useState(() => {
    // Light mode is ALWAYS the default. Dark only when the user has
    // explicitly toggled it on (never from OS/system preference — that was
    // making phones with OS dark mode render the whole app dark).
    return localStorage.getItem('corvana-dark-mode') === 'true';
  });

  // Apply dark class to html element. Dark mode is a CRM/app feature only —
  // the public marketing site is ALWAYS light. Gating by path here (rather than
  // relying on PublicHeader to strip the class) fixes the initial-load race:
  // React runs the deep PublicHeader effect BEFORE this root provider effect,
  // so on a hard refresh the provider used to re-add `dark` and darken the
  // marketing site on phones with a stored/OS dark preference.
  useEffect(() => {
    const root = document.documentElement;
    const onCrm = typeof window !== 'undefined'
      && window.location.pathname.startsWith('/corvana-crm');
    if (isDark && onCrm) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('corvana-dark-mode', isDark.toString());
  }, [isDark]);

  // Re-evaluate on client-side route changes so entering the CRM re-applies a
  // stored dark preference, and leaving it forces the public site back to light.
  useEffect(() => {
    const apply = () => {
      const root = document.documentElement;
      const onCrm = window.location.pathname.startsWith('/corvana-crm');
      if (isDark && onCrm) root.classList.add('dark');
      else root.classList.remove('dark');
    };
    window.addEventListener('popstate', apply);
    const id = setInterval(apply, 800);
    return () => { window.removeEventListener('popstate', apply); clearInterval(id); };
  }, [isDark]);

  const toggle = useCallback(() => {
    setIsDark(prev => !prev);
  }, []);

  const setMode = useCallback((dark) => {
    setIsDark(dark);
  }, []);

  return (
    <DarkModeContext.Provider value={{ isDark, toggle, setMode }}>
      {children}
    </DarkModeContext.Provider>
  );
}

export default DarkModeProvider;
